import React, { createContext, useContext, ReactNode, useState } from 'react';
import { DescriptionOutlined, BeenhereOutlined, SchoolOutlined } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import useSolutions from './_data_sources/useSolutions';
import useOrganizationSolutions from './_data_sources/useOrganizationSolutions';
import useManageSolutions from './_data_sources/useManageSolutions';
import { useOrgRiskPoolStatus, useOrgRiskPoolMutations } from './_data_sources/useRiskPool';
import { Solution, SolutionWithActive } from './_types/solution';
import { AppContext } from '../../../context';

interface ManageSolutionsContextType {
  allSolutions: Solution[] | undefined;
  orgSolutions: Solution[] | undefined;
  isLoadingAllSolutions: boolean;
  isLoadingOrgSolutions: boolean;
  allSolutionsError: any;
  orgSolutionsError: any;
  getSolutionIdByName: (name: string) => string | undefined;
  isSolutionAssignedToOrg: (solutionId: string) => boolean;
  getIcon: (solutionKey: string) => React.ReactNode;
  getSolutionByKey: (solutionKey: string) => SolutionWithActive | undefined;
  isSolutionActive: (solutionKey: string) => boolean;
  toggleSolutionByKey: (solutionKey: string, checked: boolean) => Promise<void>;
  uiIsLoading: boolean;
  setUiIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  uiIsSaving: boolean;
  setUiIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSolution: SolutionWithActive | null;
  setSelectedSolution: React.Dispatch<React.SetStateAction<SolutionWithActive | null>>;
  onSolutionToggle: (solution: SolutionWithActive, checked: boolean) => Promise<void>;
  orgRiskPoolEnabled: boolean;
  isLoadingOrgRiskPool: boolean;
  isMutatingOrgRiskPool: boolean;
  toggleOrgRiskPool: (enabled: boolean) => Promise<void>;
}

const ManageSolutionsContext = createContext<ManageSolutionsContextType | undefined>(undefined);

interface ManageSolutionsProviderProps {
  children: ReactNode;
}

export const ManageSolutionsProvider: React.FC<ManageSolutionsProviderProps> = ({ children }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const confirm = useConfirm();

  const { data: allSolutionsData, isLoading: isLoadingAllSolutions, error: allSolutionsError } = useSolutions();

  const {
    data: orgSolutions,
    isLoading: isLoadingOrgSolutions,
    error: orgSolutionsError,
  } = useOrganizationSolutions(currentOrganizationId);

  const { addSolutionToOrganization, removeSolutionFromOrganization } = useManageSolutions();

  const { data: orgRiskPoolEnabled = false, isLoading: isLoadingOrgRiskPool } = useOrgRiskPoolStatus(currentOrganizationId);
  const { enableOrgRiskPool, disableOrgRiskPool } = useOrgRiskPoolMutations();

  const isMutatingOrgRiskPool = enableOrgRiskPool.isLoading || disableOrgRiskPool.isLoading;

  // Extract the content array from allSolutionsData
  const allSolutions = allSolutionsData?.content;

  // Shared UI state for module
  const [uiIsLoading, setUiIsLoading] = useState<boolean>(true);
  const [uiIsSaving, setUiIsSaving] = useState<boolean>(false);
  const [selectedSolution, setSelectedSolution] = useState<SolutionWithActive | null>(null);

  // Handle solution toggle
  const onSolutionToggle = async (solution: SolutionWithActive, checked: boolean) => {
    setUiIsSaving(true);
    if (checked) {
      try {
        await addSolutionToOrganization.mutateAsync({
          orgId: currentOrganizationId,
          solutionId: solution.id,
        });
        setSelectedSolution((prev) => (prev ? { ...prev, active: true } : prev));
      } catch (error) {
        console.error('Error adding solution:', error);
      } finally {
        setUiIsSaving(false);
      }
    } else {
      try {
        await confirm({
          title: t('manageSolutions.disableSolution.title'),
          description: t('manageSolutions.disableSolution.description'),
          confirmationText: t('manageSolutions.disableSolution.confirm'),
          cancellationText: t('manageSolutions.disableSolution.cancel'),
        });

        await removeSolutionFromOrganization.mutateAsync({
          orgId: currentOrganizationId,
          solutionId: solution.id,
        });
        setSelectedSolution((prev) => (prev ? { ...prev, active: false } : prev));
      } catch (error) {
        console.error('Error toggling solution:', error);
      } finally {
        setUiIsSaving(false);
      }
    }
  };

  // Helper function to get solution ID by name (case-insensitive)
  const getSolutionIdByName = (name: string): string | undefined => {
    if (!allSolutions) return undefined;

    const solution = allSolutions.find((solution) => solution.name.toLowerCase() === name.toLowerCase());

    return solution?.id;
  };

  // Helper function to check if a solution is assigned to the current organization
  const isSolutionAssignedToOrg = (solutionId: string): boolean => {
    if (!orgSolutions) return false;

    return orgSolutions.some((solution) => solution.id === solutionId);
  };

  // Helper function to get icon based on solution key
  const getIcon = (solutionKey: string): React.ReactNode => {
    const key = (solutionKey || '').toLowerCase();
    if (key === 'training') {
      return (
        <SchoolOutlined
          width={28}
          height={28}
        />
      );
    } else if (key === 'awb') {
      return (
        <BeenhereOutlined
          width={28}
          height={28}
        />
      );
    } else if (key === 'policy') {
      return (
        <DescriptionOutlined
          width={28}
          height={28}
        />
      );
    }
    // Default icon
    return (
      <DescriptionOutlined
        width={28}
        height={28}
      />
    );
  };

  // Helper function to get solution by key with active status
  const getSolutionByKey = (solutionKey: string): SolutionWithActive | undefined => {
    if (!allSolutions) return undefined;

    const solution = allSolutions.find((solution) => solution.key.toLowerCase() === solutionKey.toLowerCase());
    if (!solution) return undefined;

    const active = isSolutionAssignedToOrg(solution.id);
    return { ...solution, active };
  };

  // Helper function to check if solution is active by key
  const isSolutionActive = (solutionKey: string): boolean => {
    const solution = getSolutionByKey(solutionKey);
    return solution?.active ?? false;
  };

  // Helper function to toggle solution by key
  const toggleSolutionByKey = async (solutionKey: string, checked: boolean): Promise<void> => {
    const solution = getSolutionByKey(solutionKey);
    if (!solution) {
      console.error(`Solution with key "${solutionKey}" not found`);
      return;
    }
    await onSolutionToggle(solution, checked);
  };

  // Handle risk pool toggle
  const toggleOrgRiskPool = async (enabled: boolean): Promise<void> => {
    try {
      if (enabled) {
        await enableOrgRiskPool.mutateAsync(currentOrganizationId);
      } else {
        await disableOrgRiskPool.mutateAsync(currentOrganizationId);
      }
    } catch (error) {
      console.error('Error toggling risk pool:', error);
      throw error;
    }
  };

  const contextValue: ManageSolutionsContextType = {
    allSolutions,
    orgSolutions,
    isLoadingAllSolutions,
    isLoadingOrgSolutions,
    allSolutionsError,
    orgSolutionsError,
    getSolutionIdByName,
    isSolutionAssignedToOrg,
    getIcon,
    getSolutionByKey,
    isSolutionActive,
    toggleSolutionByKey,
    uiIsLoading,
    setUiIsLoading,
    uiIsSaving,
    setUiIsSaving,
    selectedSolution,
    setSelectedSolution,
    onSolutionToggle,
    orgRiskPoolEnabled,
    isLoadingOrgRiskPool,
    isMutatingOrgRiskPool,
    toggleOrgRiskPool,
  };

  return <ManageSolutionsContext.Provider value={contextValue}>{children}</ManageSolutionsContext.Provider>;
};

export const useManageSolutionsContext = (): ManageSolutionsContextType => {
  const context = useContext(ManageSolutionsContext);
  if (context === undefined) {
    throw new Error('useManageSolutionsContext must be used within a ManageSolutionsProvider');
  }
  return context;
};
